import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PublicHeader from '../../components/navigation/PublicHeader';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const ServiceRequestConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const requestData = location?.state || {};

  useEffect(() => {
    if (!requestData?.requestNumber) {
      navigate('/');
    }
  }, [requestData, navigate]);

  const getUrgencyColor = (urgency) => {
    return urgency === 'Emergency' ? 'bg-error/10 text-error border-error/20' : 'bg-warning/10 text-warning border-warning/20';
  };

  const getUrgencyIcon = (urgency) => {
    return urgency === 'Emergency' ? 'AlertTriangle' : 'Clock';
  };

  const getEstimatedResponse = (urgency) => {
    return urgency === 'Emergency' ? '30-60 minutes' : '2-4 hours';
  };

  const handleCallDispatch = () => {
    window.location.href = 'tel:+18005550100';
  };

  const handleNewRequest = () => {
    navigate('/');
  };

  const handleShare = () => {
    const message = `Service Request ${requestData?.requestNumber}\nUnit: ${requestData?.unitNumber}\nUrgency: ${requestData?.urgency}\nIssue: ${requestData?.issueType}`;
    
    if (navigator?.share) {
      navigator.share({
        title: 'Service Request Confirmation',
        text: message
      });
    } else {
      navigator.clipboard?.writeText(message);
      alert('Request details copied to clipboard');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <main className="pt-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="max-w-3xl mx-auto">
            <div className="bg-card border border-border rounded-lg shadow-elevation-2 overflow-hidden">
              <div className="bg-success/10 border-b border-success/20 px-6 py-8 text-center">
                <div className="flex items-center justify-center w-16 h-16 bg-success/20 rounded-full mx-auto mb-4">
                  <Icon name="CheckCircle2" size={32} color="var(--color-success)" strokeWidth={2} />
                </div>
                <h1 className="text-2xl font-semibold text-foreground mb-2">Request Submitted Successfully</h1>
                <p className="text-sm text-muted-foreground">
                  Your service request has been received and is being processed
                </p>
              </div>

              <div className="p-6 space-y-6">
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Request Number</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Copy"
                      onClick={() => {
                        navigator.clipboard?.writeText(requestData?.requestNumber);
                        alert('Request number copied!');
                      }}
                    />
                  </div>
                  <p className="text-2xl font-bold text-primary">{requestData?.requestNumber}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date()?.toLocaleString()}
                  </p>
                </div>

                <div>
                  <h2 className="text-lg font-semibold text-foreground mb-3 flex items-center">
                    <Icon name="FileText" size={20} className="mr-2" />
                    Request Summary
                  </h2>
                  <div className="space-y-3">
                    <div className="flex items-start justify-between p-3 bg-muted/30 rounded-lg">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Unit Number</p>
                        <p className="text-sm font-semibold text-foreground">{requestData?.unitNumber}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground mb-1">VIN</p>
                        <p className="text-sm font-mono text-foreground">{requestData?.vin}</p>
                      </div>
                    </div>

                    <div className="p-3 bg-muted/30 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Yard Location</p>
                      <p className="text-sm font-semibold text-foreground">{requestData?.yardName}</p>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Service Urgency</p>
                        <div className={`inline-flex items-center space-x-2 px-3 py-1.5 rounded-full border ${getUrgencyColor(requestData?.urgency)}`}>
                          <Icon name={getUrgencyIcon(requestData?.urgency)} size={14} strokeWidth={2} />
                          <span className="text-xs font-medium">{requestData?.urgency}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground mb-1">Issue Type</p>
                        <p className="text-sm font-semibold text-foreground">{requestData?.issueType}</p>
                      </div>
                    </div>

                    <div className="p-3 bg-muted/30 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Description</p>
                      <p className="text-sm text-foreground">{requestData?.description}</p>
                    </div>

                    {requestData?.partsNeeded && (
                      <div className="p-3 bg-muted/30 rounded-lg">
                        <p className="text-xs text-muted-foreground mb-1">Parts Needed</p>
                        <p className="text-sm text-foreground">{requestData?.partsNeeded}</p>
                      </div>
                    )}

                    {requestData?.photoCount > 0 && (
                      <div className="p-3 bg-muted/30 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Icon name="Camera" size={16} color="var(--color-primary)" />
                          <p className="text-sm text-foreground">
                            {requestData?.photoCount} photo{requestData?.photoCount > 1 ? 's' : ''} attached
                          </p>
                        </div>
                      </div>
                    )}

                    {requestData?.location && (
                      <div className="p-3 bg-muted/30 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <Icon name="MapPin" size={16} color="var(--color-success)" className="mt-0.5" />
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">GPS Location</p>
                            <p className="text-sm text-foreground">{requestData?.location?.address}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      Your request has been submitted to your yard manager and RPM. The request is pending your yard manager's approval.
                    </p>
                  </div>
                </div>

                <div>
                  <h2 className="text-lg font-semibold text-foreground mb-3 flex items-center">
                    <Icon name="Phone" size={20} className="mr-2" />
                    Contact Information
                  </h2>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Dispatch Center</p>
                        <a href="tel:+18005550100" className="text-sm font-medium text-primary hover:underline">
                          (800) 555-0100
                        </a>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Phone"
                        onClick={handleCallDispatch}
                      >
                        Call Now
                      </Button>
                    </div>
                    <div className="p-3 bg-muted/30 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Emergency Contact</p>
                      <a href="tel:+18005550200" className="text-sm font-medium text-primary hover:underline">
                        (800) 555-0200
                      </a>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-4 border-t border-border">
                  <Button
                    variant="outline"
                    size="lg"
                    iconName="Share2"
                    iconPosition="left"
                    onClick={handleShare}
                  >
                    Share
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    iconName="Plus"
                    iconPosition="left"
                    onClick={handleNewRequest}
                  >
                    New Request
                  </Button>
                  <Button
                    variant="default"
                    size="lg"
                    iconName="Home"
                    iconPosition="left"
                    onClick={() => navigate('/')}
                  >
                    Back Home
                  </Button>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-warning/10 border border-warning/20 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="Info" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">What Happens Next?</p>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>• Dispatch will review your request and assign a technician</li>
                    <li>• You will receive a call or text with the estimated arrival time</li>
                    <li>• Keep your phone nearby for updates</li>
                    <li>• For emergencies, call dispatch directly at (800) 555-0100</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ServiceRequestConfirmation;